import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils/formatters';

const SellerListModal = ({ 
  isOpen, 
  onClose, 
  item, 
  isComponent = false, 
  onAddToCart,
  onViewDetails,
  isAuthenticated,
  onAuthRequired
}) => {
  const [sellers, setSellers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen && item) {
      fetchSellers();
    }
  }, [isOpen, item]);

  const fetchSellers = async () => {
    setIsLoading(true);
    try {
      let data = [];
      if (isComponent) {
        const { data: sc } = await supabase
          .from('seller_components')
          .select(`
            id, price, stock_quantity, purchase_limit, custom_name, is_self_manufacturing, manufacturer_id,
            seller:seller_accounts(id, business_name, business_slug, rating)
          `)
          .eq('component_id', item.id);
          // seller_components doesn't have an explicit status=active, we rely on isdeleted
        data = sc || [];
      } else {
        const { data: sp } = await supabase
          .from('seller_products')
          .select(`
            id, price, stock_amount, purchase_limit, vin_sn, is_self_manufacturing, manufacturer_id,
            seller:seller_accounts(id, business_name, business_slug, rating)
          `)
          .eq('product_id', item.id)
          .eq('status', 'active')
          .gt('price', 0);
        data = sp || [];
      }

      setSellers(data.sort((a, b) => (a.price || 0) - (b.price || 0)));
    } catch (err) {
      console.error('Error fetching sellers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = async (sellerOffering) => {
    if (!isAuthenticated) {
      onAuthRequired('Please log in to add items to cart.');
      return;
    }

    const isMfg = sellerOffering.is_self_manufacturing || !!sellerOffering.manufacturer_id;
    if (isMfg && onViewDetails) {
      onViewDetails(sellerOffering);
      return;
    }
    
    // We pass the exact seller ID along with the item to the cart
    const cartItem = isComponent ? {
      ...item,
      sellerId: sellerOffering.seller.id,
      sellerComponentId: sellerOffering.id,
      globalComponentId: item.id, // For component
      price: sellerOffering.price
    } : {
      ...item,
      sellerId: sellerOffering.seller.id,
      sellerProductId: sellerOffering.id,
      price: sellerOffering.price
    };

    await onAddToCart(cartItem);
    onClose();
  };

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="bg-card border border-border shadow-2xl rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col relative z-10 animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground line-clamp-1">{item.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">Available from {sellers.length} seller{sellers.length !== 1 ? 's' : ''}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4" />
              <p className="text-muted-foreground">Finding sellers...</p>
            </div>
          ) : sellers.length > 0 ? (
            <div className="space-y-4">
              {sellers.map((offering) => {
                const stockQty = offering.stockQuantity ?? offering.stock_quantity ?? offering.stockAmount ?? offering.stock_amount ?? 0;
                const isMfg = offering.is_self_manufacturing || !!offering.manufacturer_id;
                return (
                  <div key={offering.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-border rounded-lg bg-background hover:border-primary/50 transition-colors">
                    <div className="mb-4 sm:mb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="Store" size={16} className="text-primary" />
                        <h3 className="font-semibold text-base">{offering.seller?.business_name || 'Direct Seller'}</h3>
                      </div>
                      {offering.vin_sn && (
                        <div className="mb-2">
                          <span className="text-[10px] text-gray-500 font-mono px-2 py-0.5 bg-gray-100 rounded-sm border border-gray-200">VIN: {offering.vin_sn}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                         <span className="flex items-center">
                           <Icon name="Star" size={14} className="mr-1 text-yellow-500" />
                           {offering.seller?.rating ? offering.seller.rating.toFixed(1) : 'No rating'}
                         </span>
                         {isMfg ? (
                           <span className="text-orange-600 flex items-center font-bold">
                             <Icon name="Zap" size={14} className="mr-1" />
                             Custom Manufactured
                           </span>
                         ) : (
                           <span className="text-success flex items-center gap-2">
                             <Icon name="Check" size={14} className="mr-1" />
                             {offering.vinSn ? 'In Stock' : `In Stock (${stockQty})`}
                           </span>
                         )}
                      </div>
                    </div>
                  
                  <div className="flex items-center justify-between sm:flex-col sm:items-end gap-3 sm:gap-2 pt-4 sm:pt-0">
                    <span className="text-xl font-bold text-foreground">
                      {formatPrice(offering.price)}
                    </span>
                    <Button 
                      variant="default" 
                      size="sm" 
                      iconName="ShoppingCart"
                      onClick={() => handleAddToCart(offering)}
                      className="whitespace-nowrap w-full sm:w-auto"
                    >
                      Add to Cart
                    </Button>
                  </div>
                </div>
              )})}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="PackageX" size={48} className="mx-auto text-muted-foreground mb-4 opacity-50" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Active Sellers</h3>
              <p className="text-muted-foreground">This item is currently not offered directly by any seller.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerListModal;
